/* eslint no-undef: "off" */

import axios from 'axios'
import { createApp } from 'vue'

import './orderCurtain'
import { pushItemRemovedEvent } from '~/js/gtm'
import FButton from '~/js/components/button'
import FCartProductActions from '~/js/components/cartProductActions'
import FModal from '~/js/components/modal'
import { quoteAllItems } from '~/js/templates/cart/orderCurtain'

const ID_CART = 'js-cart'

/**
 * オーダーカーテンとオプションを削除する関数
 * オーダーカーテンは同一のvariantIdの商品がカートに複数存在する可能性があるため、variantIdとindexで対象を特定して削除する。
 * @param variantId 削除対象のオーダーカーテンのバリアントID
 * @param quantity オーダーカーテン本体の数量（通常は0になるはずだが、オプションの数量調整のために渡す）
 * @param optionRef 有料オプションのリファレンス
 * @param index 同一商品が複数ある場合のインデックス
 * @returns {Promise<void>}
 */
const removeOrderCurtainAndOptions = async ({
  variantId,
  quantity,
  optionRef,
  index,
}) => {
  try {
    const cart = (await axios.get('/cart.js')).data
    const targetItem = cart.items[index]
    if (!targetItem || targetItem.variant_id !== Number(variantId)) {
      alert('該当する商品が見つかりませんでした。')
      return
    }
    const target = { item: targetItem, line: index + 1 }
    const delta = quantity - target.item.quantity
    // オーダーカーテン本体を削除
    await axios.post('/cart/change.js', {
      line: target.line,
      quantity: 0,
    })
    pushItemRemovedEvent({
      itemRemoved: target.item,
      quantity: target.item.quantity,
    })

    // オプションバリアント(有料オプション)も削除
    const optionVariantIds = optionRef
      .split(',')
      .filter((v) => v)
      .map((string) => Number(string.split(':')[1]))
    if (optionVariantIds.length) {
      const updates = {}
      optionVariantIds.forEach((optionVariantId) => {
        const optionVariantItem = cart.items.find(
          (lineItem) => lineItem.variant_id === optionVariantId
        )
        const newQuantity = optionVariantItem.quantity + delta
        const removedQuantity = optionVariantItem.quantity - newQuantity
        updates[optionVariantId] = newQuantity
        pushItemRemovedEvent({
          itemRemoved: optionVariantItem,
          quantity: removedQuantity,
        })
      })
      await axios.post('/cart/update.js', { updates })
    }
  } catch (e) {
    alert(
      '申し訳ございませんが、アイテムの削除中にエラーが発生しました。しばらくしてからもう一度お試しください。'
    )
    console.error(e)
    throw e
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  const elementCart = document.getElementById(ID_CART)
  if (!elementCart) return

  window.addEventListener('pageshow', () => {
    const { customerId, endpointPurchaseLater, hmac } = elementCart.dataset
    const isCatalogGift = elementCart.dataset.isCatalogGift == 'true'

    // ブラウザバック、またはチェックアウトからカバンボタンを押して戻った際に直前の入力内容を復元する
    const usage =
      elementCart.dataset.allInGiftUsage ||
      document.querySelector('[name=all-in-gift-usage]')?.value ||
      'なし'
    const digitalMessage =
      elementCart.dataset.allInGiftDigitalMessage ||
      document.querySelector('[name=all-in-gift-digital-message]')?.value ||
      ''
    const digitalMessageCardUrl =
      elementCart.dataset.allInGiftMessageCardUrl ||
      document.querySelector('[name=all-in-gift-message-card-url]')?.value ||
      null
    const allInGiftSelected =
      elementCart.dataset.allInGiftSelected === 'true' ||
      document.querySelector('[name=all-in-gift-selected]:checked')?.id ===
        'use' ||
      digitalMessage !== ''

    const elementsLineItems = document.querySelectorAll('[data-line-item]')
    const lineItems = Array.from(elementsLineItems).map((element, idx) => {
      const { quantity, variantId, product } = element.dataset
      const giftWrappingEligible = element.hasAttribute(
        'data-gift-wrapping-eligible'
      )
      return {
        giftWrappingEligible: giftWrappingEligible,
        product: JSON.parse(product),
        quantity: Number(quantity),
        variantId,
        index: idx,
      }
    })
    createApp({
      name: 'TemplatesCart',
      components: {
        FButton,
        FCartProductActions,
        FModal,
      },
      data() {
        return {
          vueLoaded: true,
          customerId,
          endpointPurchaseLater,
          hmac,
          isAllInGift: isCatalogGift || allInGiftSelected,
          isProcessing: false,
          usage,
          digitalMessage,
          digitalMessageCardUrl,
          showModalPreview: false,
          errorTextDigitalMessage: '',
        }
      },
      watch: {
        digitalMessage(value) {
          this.errorTextDigitalMessage =
            value.length <= 60 ? '' : '60文字以内で入力してください。'
        },
      },
      computed: {
        isPreviewAvailable() {
          return !!(this.digitalMessageCardUrl && this.digitalMessage)
        },
        allInGiftAttributes() {
          return this.isAllInGift
            ? [
                { key: 'SNSギフト', value: this.isAllInGift },
                { key: 'ギフト', value: this.isAllInGift },
                { key: 'ギフトの利用用途', value: this.usage },
                { key: 'デジタルメッセージ', value: this.digitalMessage },
                {
                  key: 'デジタルメッセージ画像URL',
                  value: this.digitalMessageCardUrl,
                },
              ]
            : []
        },
        isAllInGiftValid() {
          if (!this.isAllInGift) return true
          return (
            !!this.usage &&
            !!this.digitalMessage &&
            !!this.digitalMessageCardUrl &&
            !this.errorTextDigitalMessage
          )
        },
        isNextAvailable() {
          return this.isAllInGiftValid && !this.isProcessing
        },
      },
      methods: {
        onProcessingChange(isProcessing) {
          this.isProcessing = isProcessing
        },
        async onNextClicked(event) {
          event.preventDefault()

          if (!this.isNextAvailable) return
          this.isProcessing = true

          if (!this.allInGiftAttributes.length) {
            window.location.href = '/checkout'
            return
          }

          const formData = new FormData()
          this.allInGiftAttributes
            .filter((item) => item.value !== undefined)
            .forEach((item) =>
              formData.append(`attributes[${item.key}]`, item.value)
            )

          try {
            await axios.post('/cart', formData)
          } catch (error) {
            console.error(error)
            this.isProcessing = false
          }

          /**
           * 問題なければチェックアウトへ進む
           */
          window.location.href = '/checkout'
        },
        quoteAllItems($event) {
          quoteAllItems($event.target)
        },
        async onRemoveAllClicked() {
          const confirmed = confirm('カート内の全ての商品を削除しますか？')
          if (confirmed) {
            try {
              await axios.post('/cart/clear.js')
              window.location.reload()
            } catch (error) {
              console.error('Error clearing cart:', error)
              alert('カートの削除中にエラーが発生しました。')
            }
          }
        },
        async updateCartItems({ variantId, quantity, optionRef, index }) {
          const lineItem = lineItems.find(
            (item) => item.variantId === variantId
          )
          const updates = { [variantId]: quantity }

          // ギフトラッピング対象商品がなくなる場合はギフトラッピングも削除
          if (
            quantity === 0 &&
            lineItems
              .filter((item) => item.variantId !== variantId)
              .filter((item) => item.giftWrappingEligible).length === 0
          ) {
            const elementGiftWrapping = document.querySelector(
              '[data-is-gift-wrapping]'
            )
            if (elementGiftWrapping)
              updates[elementGiftWrapping.dataset.variantId] = 0
          }

          if (
            quantity === 0 &&
            lineItem &&
            lineItem.product &&
            lineItem.product.type === 'オーダーカーテン'
          ) {
            await removeOrderCurtainAndOptions({
              variantId,
              quantity,
              optionRef,
              index,
            })
            return
          }
          try {
            await axios.post('/cart/update.js', { updates })
            const removedVariantIds = Object.keys(updates).filter(
              (variantId) => updates[variantId] === 0
            )
            lineItems
              .filter((item) => removedVariantIds.includes(item.variantId))
              .forEach((item) => {
                pushItemRemovedEvent({
                  itemRemoved: item.product,
                  quantity: item.quantity,
                })
              })
          } catch (e) {
            alert(
              '申し訳ございませんが、アイテムの操作中にエラーが発生しました。しばらくしてからもう一度お試しください。'
            )
            console.error(e)
          }
        },
      },
    }).mount(elementCart)
  })
})
